// SPDX-License-Identifier: AGPL-3.0-or-later

import {CUSTOM_SOUND_FORMATS, CUSTOM_SOUND_MAX_SIZE_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import type * as CustomSoundDB from '@app/features/notification/utils/CustomSoundDB';
import type {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Accordion} from '@app/features/ui/accordion/Accordion';
import {Switch} from '@app/features/ui/components/form/Switch';
import {Slider} from '@app/features/ui/components/Slider';
import {SwitchGroup, SwitchGroupCustomItem} from '@app/features/ui/components/SwitchGroup';
import type {SoundSettings} from '@app/features/ui/state/Sound';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/Sounds.module.css';
import {useLingui} from '@lingui/react/macro';
import {ArrowCounterClockwiseIcon, SpeakerHighIcon, TrashIcon, UploadIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

interface SoundsProps {
	soundSettings: SoundSettings;
	soundTypeLabels: Record<SoundType, string>;
	customSounds: Record<SoundType, CustomSoundDB.CustomSound | null>;
	isSoundEnabled: (soundType: SoundType) => boolean;
	onToggleAllSounds: (value: boolean) => void;
	onToggleSound: (soundType: SoundType, enabled: boolean) => void;
	onEnableAllSounds: () => void;
	onDisableAllSounds: () => void;
	onPreviewSound: (soundType: SoundType) => void;
	onUploadClick: (soundType: SoundType) => void;
	onCustomSoundDelete: (soundType: SoundType) => void;
	onMasterVolumeChange: (value: number) => void;
	onSoundOverrideChange: (soundType: SoundType, value: number) => void;
	onSoundOverrideReset: (soundType: SoundType) => void;
	onAllOverridesReset: () => void;
}

const DEFAULT_MASTER = 100;
export const Sounds: React.FC<SoundsProps> = observer(
	({
		soundSettings,
		soundTypeLabels,
		customSounds,
		isSoundEnabled,
		onToggleAllSounds,
		onToggleSound,
		onEnableAllSounds,
		onDisableAllSounds,
		onPreviewSound,
		onUploadClick,
		onCustomSoundDelete,
		onMasterVolumeChange,
		onSoundOverrideChange,
		onSoundOverrideReset,
		onAllOverridesReset,
	}) => {
		const {t} = useLingui();
		const masterVolume = soundSettings.masterVolume ?? DEFAULT_MASTER;
		const soundOverrides = soundSettings.soundOverrides ?? {};
		const overrideCount = Object.keys(soundOverrides).length;
		const hasAnyOverride = overrideCount > 0;
		const allDisabled = soundSettings.allSoundsDisabled;
		const [accordionExpanded, setAccordionExpanded] = useState(hasAnyOverride);
		const previousHadOverridesRef = useRef(hasAnyOverride);
		useEffect(() => {
			if (hasAnyOverride && !previousHadOverridesRef.current) {
				setAccordionExpanded(true);
			}
			previousHadOverridesRef.current = hasAnyOverride;
		}, [hasAnyOverride]);
		return (
			<div className={styles.container}>
				<p className={styles.description}>{t`Configure which sounds to play and when.`}</p>
				<div className={styles.content}>
					<div className={styles.masterRow}>
						<div className={styles.masterHeader}>
							<span className={styles.masterLabel}>{t`Master Volume`}</span>
							<span className={styles.masterValue}>{`${masterVolume}%`}</span>
						</div>
						<p className={styles.masterDescription}>
							{t`Sets the level for every sound effect. Per-sound overrides ignore this.`}
						</p>
						<Slider
							value={masterVolume}
							defaultValue={masterVolume}
							factoryDefaultValue={DEFAULT_MASTER}
							minValue={0}
							maxValue={200}
							step={1}
							markers={[0, 50, 100, 150, 200]}
							disabled={allDisabled}
							onMarkerRender={(value) => `${value}%`}
							onValueRender={(value) => `${value}%`}
							onValueChange={onMasterVolumeChange}
							showResetButton={true}
							resetTooltip={t`Reset to default volume`}
						/>
					</div>
					<Switch
						label={t`Disable all notification sounds`}
						description={t`Your existing notification sound settings will be preserved.`}
						value={allDisabled}
						onChange={onToggleAllSounds}
					/>
					<div className={styles.hint}>
						{t`Click the upload icon next to any sound to customize it with your own audio file. Supported formats: ${CUSTOM_SOUND_FORMATS} (max ${CUSTOM_SOUND_MAX_SIZE_LABEL}).`}
					</div>
					<SwitchGroup>
						{Object.entries(soundTypeLabels).map(([soundType, label]) => {
							const type = soundType as SoundType;
							return (
								<SwitchGroupCustomItem
									key={soundType}
									label={
										<div className={styles.soundLabel}>
											<div className={styles.soundLabelTitle}>
												<span>{label}</span>
												{customSounds[type] && <span className={styles.customBadge}>Custom</span>}
											</div>
											<button
												type="button"
												className={styles.previewLink}
												onClick={() => onPreviewSound(type)}
												disabled={allDisabled}
											>{t`Preview sound`}</button>
										</div>
									}
									value={allDisabled ? false : isSoundEnabled(type)}
									disabled={allDisabled}
									onChange={(enabled) => onToggleSound(type, enabled)}
									extraContent={
										<>
											<button
												type="button"
												onClick={() => onUploadClick(type)}
												disabled={allDisabled}
												className={styles.iconButton}
												aria-label={t`Upload custom sound for ${label}`}
											>
												<UploadIcon size={16} className={styles.uploadIcon} />
											</button>
											{customSounds[type] && (
												<button
													type="button"
													onClick={() => onCustomSoundDelete(type)}
													disabled={allDisabled}
													className={styles.iconButton}
													aria-label={t`Remove custom sound for ${label}`}
												>
													<TrashIcon size={16} className={styles.deleteIcon} />
												</button>
											)}
										</>
									}
								/>
							);
						})}
					</SwitchGroup>
					<div className={styles.actionsContainer}>
						<button type="button" className={styles.actionButton} onClick={onEnableAllSounds} disabled={allDisabled}>
							{t`Enable All`}
						</button>
						<span className={styles.actionSeparator}>•</span>
						<button type="button" className={styles.actionButton} onClick={onDisableAllSounds} disabled={allDisabled}>
							{t`Disable All`}
						</button>
					</div>
					<Accordion
						id="sound-overrides"
						title={t`Per-sound volume`}
						description={
							hasAnyOverride
								? t`${overrideCount} sound(s) override the master volume.`
								: t`Set custom volumes for individual sounds. Sounds without an override follow the master volume.`
						}
						expanded={accordionExpanded}
						onExpandedChange={setAccordionExpanded}
					>
						<div className={styles.overridesList}>
							{Object.entries(soundTypeLabels).map(([soundType, label]) => {
								const type = soundType as SoundType;
								const override = soundOverrides[type];
								const hasOverride = override !== undefined;
								const effectiveValue = hasOverride ? (override as number) : masterVolume;
								const rowDisabled = allDisabled || !isSoundEnabled(type);
								return (
									<div key={soundType} className={clsx(styles.overrideRow, !hasOverride && styles.overrideRowLinked)}>
										<div className={styles.overrideHeader}>
											<span className={styles.overrideLabel}>{label}</span>
											<button
												type="button"
												className={styles.previewButton}
												onClick={() => onPreviewSound(type)}
												aria-label={t`Preview ${label}`}
											>
												<SpeakerHighIcon size={14} weight="fill" />
											</button>
											<span className={styles.overrideStatus}>
												{hasOverride ? `${effectiveValue}%` : t`Following master • ${effectiveValue}%`}
											</span>
											<button
												type="button"
												className={clsx(styles.resetButton, !hasOverride && styles.resetButtonHidden)}
												onClick={() => onSoundOverrideReset(type)}
												disabled={rowDisabled || !hasOverride}
												aria-label={t`Reset ${label} to master volume`}
											>
												<ArrowCounterClockwiseIcon size={14} weight="bold" />
											</button>
										</div>
										<Slider
											value={effectiveValue}
											defaultValue={effectiveValue}
											factoryDefaultValue={masterVolume}
											minValue={0}
											maxValue={200}
											step={1}
											disabled={rowDisabled}
											onValueRender={(value) => `${value}%`}
											onValueChange={(value) => onSoundOverrideChange(type, value)}
										/>
									</div>
								);
							})}
						</div>
						{hasAnyOverride && (
							<div className={styles.actionsContainer}>
								<button
									type="button"
									className={styles.actionButton}
									onClick={onAllOverridesReset}
									disabled={allDisabled}
								>
									{t`Reset all overrides`}
								</button>
							</div>
						)}
					</Accordion>
				</div>
			</div>
		);
	},
);
